import { useMemo, useState } from 'react';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  nestServiceRequestsForTable,
  searchServiceRequestsGeneralUtil,
} from 'src/pages/ServiceRequests/helpers/filterServiceRequests';
import { toggleIdBatchInSelection } from 'src/pages/ServiceRequests/helpers/selectionHelpers';
import { RESOLVED_BUCKET } from 'src/pages/Property/CustomHooks/PropertyServiceRequests/constants';

export interface UseCommonAreaServiceRequestsFormStateArgs {
  serviceRequests: IServiceRequest[];
}

export interface UseCommonAreaServiceRequestsFormStateReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  selectableVisibleIds: string[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  visibleServiceRequests: IServiceRequest[];

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  reset: () => void;
}

const RESOLVED_STATUS_SET: Set<string> = new Set(RESOLVED_BUCKET);

export const useCommonAreaServiceRequestsFormState = ({
  serviceRequests,
}: UseCommonAreaServiceRequestsFormStateArgs): UseCommonAreaServiceRequestsFormStateReturn => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showResolvedOnly, setShowResolvedOnly] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [isDownloadOverlayOpen, setIsDownloadOverlayOpen] =
    useState<boolean>(false);

  const visibleServiceRequests: IServiceRequest[] = useMemo(() => {
    const searched: IServiceRequest[] = searchServiceRequestsGeneralUtil(
      serviceRequests,
      searchQuery
    );

    const resolvedRequests: IServiceRequest[] = searched.filter(
      (sr: IServiceRequest) => RESOLVED_STATUS_SET.has(sr.status)
    );
    const unresolvedRequests: IServiceRequest[] = searched.filter(
      (sr: IServiceRequest) => !RESOLVED_STATUS_SET.has(sr.status)
    );

    const statusFiltered: IServiceRequest[] = showResolvedOnly
      ? resolvedRequests
      : unresolvedRequests;

    return nestServiceRequestsForTable(statusFiltered);
  }, [serviceRequests, searchQuery, showResolvedOnly]);

  const toggleShowResolved = (): void => {
    setShowResolvedOnly((prev: boolean) => !prev);
  };

  const selectableVisibleIds: string[] = useMemo(() => {
    return visibleServiceRequests
      .filter((sr: IServiceRequest) => sr.isGroup !== true)
      .map((sr: IServiceRequest) => sr.id);
  }, [visibleServiceRequests]);

  const allVisibleSelected: boolean = useMemo(() => {
    if (selectableVisibleIds.length === 0) {
      return false;
    }

    const idSet: Set<string> = new Set(selectedIds);
    return selectableVisibleIds.every((id: string) => idSet.has(id));
  }, [selectableVisibleIds, selectedIds]);

  const idsForCsvExport: string[] = useMemo(() => {
    if (selectedIds.length > 0) {
      return selectedIds;
    }

    return visibleServiceRequests.map((sr: IServiceRequest) => sr.id);
  }, [selectedIds, visibleServiceRequests]);

  const toggleSelection = (id: string): void => {
    setSelectedIds((prev: string[]) => {
      if (prev.includes(id)) {
        return prev.filter((existingId: string) => existingId !== id);
      }

      return [...prev, id];
    });
  };

  const toggleSelectionBatch = (ids: string[]): void => {
    if (ids.length === 0) {
      return;
    }

    setSelectedIds((prev: string[]) => toggleIdBatchInSelection(prev, ids));
  };

  const toggleSelectAllVisible = (): void => {
    if (allVisibleSelected) {
      const visibleSet: Set<string> = new Set(selectableVisibleIds);
      setSelectedIds((prev: string[]) =>
        prev.filter((id: string) => !visibleSet.has(id))
      );
      return;
    }

    setSelectedIds((prev: string[]) => {
      const merged: Set<string> = new Set(prev);
      selectableVisibleIds.forEach((id: string) => merged.add(id));
      return Array.from(merged);
    });
  };

  const clearSelection = (): void => {
    setSelectedIds([]);
  };

  const toggleGroupExpansion = (id: string): void => {
    setExpandedGroupIds((prev: string[]) => {
      if (prev.includes(id)) {
        return prev.filter((existingId: string) => existingId !== id);
      }

      return [...prev, id];
    });
  };

  const openDownloadOverlay = (): void => {
    setIsDownloadOverlayOpen(true);
  };

  const closeDownloadOverlay = (): void => {
    setIsDownloadOverlayOpen(false);
  };

  const reset = (): void => {
    setSearchQuery('');
    setShowResolvedOnly(false);
    setSelectedIds([]);
    setExpandedGroupIds([]);
    setIsDownloadOverlayOpen(false);
  };

  return {
    searchQuery,
    setSearchQuery,

    showResolvedOnly,
    toggleShowResolved,

    selectedIds,
    selectableVisibleIds,
    allVisibleSelected,
    idsForCsvExport,
    toggleSelection,
    toggleSelectionBatch,
    toggleSelectAllVisible,
    clearSelection,

    expandedGroupIds,
    toggleGroupExpansion,

    visibleServiceRequests,

    isDownloadOverlayOpen,
    openDownloadOverlay,
    closeDownloadOverlay,

    reset,
  };
};
